import React, { useState } from "react";
import { CampaignCostSpacer, SumRow, HorizontalLine } from "../BookingOverview.styled.js";
import { SALES_TAXES_RATE } from "../../utils/constants.js";
import { useTranslation } from "react-i18next";
import { validateVatNumber } from "../../../utils.js";
import Coupon from "../../../Coupon/index.jsx";
import { Expand } from "../../../../../Components/Layout/index.js";
import { FieldForm } from "./Section.styled.js";
import { Input } from "../../../Previews/Components/index.js";
import { SlideInOut } from "../../../../../styles/animations/slide-in-out.js";
import { COUNTRIES_WITHOUT_TAXES } from "../../invoice-address-modal/utils.js";
import { formatIntlNumber } from "../../../../../utils/Helpers.js";

const renderTaxesDescription = (t, taxes, prepaidStateBalance, subtotal, countryCode) => {
  if (taxes) {
    return null;
  }

  if (prepaidStateBalance > 0 && prepaidStateBalance >= subtotal && countryCode === "DE") {
    return "Hier wird keine USt. erhoben, da für die Deckung des Gesamtbetrags Prepaid-Guthaben verwendet wurden";
  }

  return t("No sales tax is charged here because of the reverse charge procedure");
};

const SumItem = ({ title, amount, isBold = false, isFinal = false, symbol = "" }) => {
  return (
    <SumRow isBold={isBold} isFinal={isFinal}>
      <p>{title}</p>
      <h4>
        {`${symbol}${amount}`}
        <span> €</span>
      </h4>
    </SumRow>
  );
};

export const SumSection = ({
  credit,
  imagePrices = 0,
  videoPrices = 0,
  campaignBudget,
  taxes,
  subtotal,
  countryCode,
  discountData,
  isAgency,
  total,
  setVatNumber,
  organization,
  combinedBudget,
  selectedChannels,
  setCoupon,
  addOns,
  language
}) => {
  const { t } = useTranslation();

  const { prepaidStateBalance = 0 } = organization;

  const { optimizeAnalysis, feedbackAnalysis, spotifyHumanVoice } = addOns;

  const [vatNumberValid, setVATNumberValid] = useState(true);
  const [showVatNumber, setShowVatNumber] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);

  const handleValidateVatNumber = (value) => {
    const isValid = validateVatNumber(value);
    setVATNumberValid(isValid);

    if (isValid) {
      setVatNumber(value);
    }
  };

  const displayCoupon = () => {
    if (!isAgency) {
      return (
        <Expand show={showCoupon} setShow={setShowCoupon} text={t("Add coupon")}>
          <FieldForm>
            <Coupon
              isEnabled={Number(prepaidStateBalance) <= 0}
              setShowCoupon={setShowCoupon}
              organization={organization}
              lifeTimeBudget={combinedBudget}
              selectedChannels={selectedChannels}
              onChange={setCoupon}
            />
          </FieldForm>
        </Expand>
      );
    }
  };

  const wantsOptimiseReview = optimizeAnalysis != null;
  const wantsFeedbackReview = feedbackAnalysis != null;
  const wantsHumanVoice = spotifyHumanVoice != null;

  return (
    <>
      <CampaignCostSpacer pushSides>
        <SumItem
          title={t("Total net")}
          amount={formatIntlNumber(campaignBudget, { language, points: 2 })}
          isBold
        />

        {imagePrices !== 0 ? (
          <SumItem
            title={t("Shutterstock images")}
            amount={formatIntlNumber(imagePrices, { language, points: 2 })}
            symbol="+"
          />
        ) : null}

        {videoPrices !== 0 ? (
          <SumItem
            title={t("Shutterstock videos")}
            amount={formatIntlNumber(videoPrices, { language, points: 2 })}
            symbol="+"
          />
        ) : null}

        <SlideInOut isOpen={wantsOptimiseReview} marginTop="0">
          <SumItem
            title={t("Analysis for Conversion Optimization")}
            amount={
              wantsOptimiseReview ? formatIntlNumber(optimizeAnalysis, { language, points: 2 }) : 0
            }
            symbol="+"
          />
        </SlideInOut>

        <SlideInOut isOpen={wantsFeedbackReview} marginTop="0">
          <SumItem
            title={t("Analysis of Your Campaign")}
            amount={
              wantsFeedbackReview ? formatIntlNumber(feedbackAnalysis, { language, points: 2 }) : 0
            }
            symbol="+"
          />
        </SlideInOut>

        <SlideInOut isOpen={wantsHumanVoice} marginTop="0">
          <SumItem
            title={t("Professional recording for Spotify")}
            amount={
              wantsHumanVoice ? formatIntlNumber(spotifyHumanVoice, { language, points: 2 }) : 0
            }
            symbol="+"
          />
        </SlideInOut>

        {credit ? (
          <SumItem
            title={t("Available credit")}
            amount={formatIntlNumber(credit, { language, points: 2 })}
            symbol="-"
          />
        ) : null}

        {COUNTRIES_WITHOUT_TAXES.includes(countryCode) ? (
          <Expand show={showVatNumber} setShow={setShowVatNumber} text={t("VAT number")}>
            <FieldForm clickable>
              <Input
                id="vat-number"
                clickIcon={() => setShowVatNumber(false)}
                label={t("VAT number")}
                placeholder="CHE123456789"
                onBlur={handleValidateVatNumber}
                error={!vatNumberValid}
                errorMessage={t("Please, ensure that the VAT number added is correct")}
              />
            </FieldForm>
          </Expand>
        ) : null}

        {displayCoupon()}

        {discountData && (
          <>
            {discountData.discountType === "percent" && (
              <SumItem
                title={`${t("Discount")} ${discountData.discount}% ${isAgency ? "AE" : ""} - ${
                  discountData.discountItemName
                }`}
                amount={formatIntlNumber(discountData.deduction, { language, points: 2 })}
                symbol="-"
              />
            )}

            {discountData.discountType === "absolute" && (
              <SumItem
                title={`${t("Discount")} - ${discountData.discountItemName}`}
                amount={formatIntlNumber(discountData.deduction, { language, points: 2 })}
                symbol="-"
              />
            )}
          </>
        )}
      </CampaignCostSpacer>

      <HorizontalLine dotted />

      <CampaignCostSpacer pushSides>
        <SumItem
          title={t("Subtotal")}
          amount={formatIntlNumber(subtotal, { language, points: 2 })}
          isBold
        />

        {taxes > 0 ? (
          <SumItem
            title={
              <span
                data-tip={renderTaxesDescription(
                  t,
                  taxes,
                  prepaidStateBalance,
                  subtotal,
                  countryCode
                )}
              >
                {t("VAT {{percent}}%", { percent: SALES_TAXES_RATE * 100 })}
              </span>
            }
            amount={formatIntlNumber(taxes, { language, points: 2 })}
            symbol="+"
          />
        ) : null}
      </CampaignCostSpacer>

      <HorizontalLine margin="32px 0 16px" />

      <CampaignCostSpacer pushSides>
        <SumItem
          title={t("Invoice amount")}
          amount={formatIntlNumber(total, { language, points: 2 })}
          isFinal
        />
      </CampaignCostSpacer>
    </>
  );
};
